import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Card } from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Colors } from '@auteco/theme'
import {
  getUserGroupsProfile,
  getUserMaterialsGroup,
  getUserMaterialsSummary,
  getUserProfiles,
  getDataByGroupId,
  sendFile,
  getFile,
  getFiltersEcualizador,
  resumenGrupo,
} from '@auteco/services/snopServices'

import { PendingMaterialsTable } from './components/PendingMaterialsTable'
import { SummarySection } from './components/SummarySection'
import { ReturnButton } from '../ReturnButton'
import { DetailsPage } from '../DetailsPage'
import { FinalValuesTable } from '../ManagerSection/components/FinalValuesTable'

const buttonStyles = {
  base: {
    background: '#4c75aa',
    padding: '8px 16px',
    fontSize: '14px',
    color: 'white',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background 0.3s ease',
  },
  hover: '#3c5d8a',
}

const uploadStatusStyles = {
  container: {
    position: 'absolute',
    top: '100%',
    left: '0',
    marginTop: '8px',
    padding: '8px 12px',
    borderRadius: '4px',
    color: 'white',
    fontSize: '14px',
    zIndex: 1000,
  },
  success: { backgroundColor: '#4caf50' },
  error: { backgroundColor: '#f44336' },
}

const CategorySection = ({ handleReturnHome }) => {
  const fileInputRef = useRef(null)
  const [managerVisible, setManagerVisible] = useState(true)
  const [detailsVisible, setDetailsVisible] = useState(false)
  const [profiles, setProfiles] = useState([])
  const [groups, setGroups] = useState([])
  const [materials, setMaterials] = useState([])
  const [currentMaterial, setCurrentMateral] = useState('')
  const [summary, setSummary] = useState()
  const [ids, setIds] = useState({ profile: '', group: '' })
  const [materialChanged, setMaterialChanged] = useState(0)
  const [data, setData] = useState('no')
  const [tableData, setTableData] = useState([])
  const [filters, setFilters] = useState([])
  const [isLoadingFile, setIsLoadingFile] = useState(false)
  const [uploadStatus, setUploadStatus] = useState({ show: false, success: false, message: '' })

  const getFilters = async () => {
    const { filtros } = await getFiltersEcualizador()
    setFilters(filtros)
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setIsLoadingFile(true)
    const formData = new FormData()
    formData.append('archivo', file)
    formData.append('grupo_id', ids.group)

    try {
      const response = await sendFile(formData)
      setUploadStatus({
        show: true,
        success: !!response,
        message: response ? 'Archivo subido exitosamente' : 'Error al subir el archivo',
      })
    } catch (error) {
      console.error('Error al enviar datos:', error)
      setUploadStatus({ show: true, success: false, message: 'Error al subir el archivo' })
    } finally {
      setIsLoadingFile(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setTimeout(() => setUploadStatus({ show: false, success: false, message: '' }), 3000)
    }
  }

  const handleDownloadExcel = async () => {
    try {
      const response = await getFile(ids.group)
      const lines = response.data.split('\n')
      const headers = lines[0].split(';')

      const rows = lines.slice(1).map(line => {
        const values = line.split(';');
        const row = {};
        headers.forEach((header, index) => {
          const value = values[index];
          if (value === '') {
            row[header] = null;
          } else if (header.toLowerCase().includes('codigo') || header.toLowerCase().includes('code')) {
            // Keep codes as text
            row[header] = value;
          } else {
            // Check if the value is a number
            const numValue = Number(value.replace(',', '.'));
            row[header] = Number.isNaN(numValue) ? value : numValue;
          }
        });
        return row;
      });
  
      // Create a new workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Datos');
  
      // Add headers
      worksheet.addRow(headers);
  
      // Add data rows and set number format for numeric columns
      rows.forEach(row => {
        const rowValues = headers.map(header => row[header]);
        const excelRow = worksheet.addRow(rowValues);
        
        // Set number format for numeric cells (without decimals)
        rowValues.forEach((value, colIndex) => {
          if (typeof value === 'number') {
            const cell = excelRow.getCell(colIndex + 1);
            cell.numFmt = '#,##0';
          }
        });
      });
  
      // Auto-fit columns
      worksheet.columns.forEach(column => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, cell => {
          const columnLength = cell.value ? cell.value.toString().length : 10;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = maxLength < 10 ? 10 : maxLength + 2;
      });
  
      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'datos_productos.xlsx');
    } catch (error) {
      console.error('Error al procesar y descargar el archivo:', error)
    }
  }

  const handleMaterialChanged = () => setMaterialChanged(prev => prev + 1)
  const handleReturnMainPage = () => {
    setManagerVisible(true)
    setDetailsVisible(false)
  }

  const handleViewDetails = ({ material }) => {
    setCurrentMateral(material)
    setManagerVisible(false)
    setDetailsVisible(true)
  }

  const getProfiles = async () => {
    const { perfiles } = await getUserProfiles()
    if (perfiles) setProfiles(perfiles)
  }

  const getGroups = async ({ id }) => {
    setMaterials([])
    setSummary(undefined)
    setIds({ profile: id, group: '' })
    
    if (id === '') {
      setGroups([])
      return
    }
    
    const { grupos } = await getUserGroupsProfile({ id })
    if (grupos) setGroups(grupos)
  }

  const getMaterials = async ({ id }) => {
    setMaterials([])
    setIds(prev => ({ ...prev, group: id }))
    
    if (id === '') {
      setSummary(undefined)
      return
    }
    
    const [materialsResponse, summaryResponse] = await Promise.all([
      getUserMaterialsGroup({ id }),
      getUserMaterialsSummary({ id })
    ])

    if (materialsResponse?.materiales) setMaterials(materialsResponse.materiales)
    if (summaryResponse) setSummary(summaryResponse)
  }

  const handleGetDataByGroupId = async ({ id }) => {
    const [response, resumenResponse] = await Promise.all([
      getDataByGroupId({ id }),
      resumenGrupo(id)
    ])

    if (resumenResponse) setTableData(resumenResponse)
    await getFilters()
    setData(response)
  }

  useEffect(() => {
    getProfiles()
  }, [])

  return (
    <>
      <div className={isLoadingFile ? 'my-loading' : ''} style={{ display: managerVisible ? 'block' : 'none' }}>
        <ReturnButton onClick={handleReturnHome} />
        <br />
        {ids.group && (
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={handleDownloadExcel}
              style={buttonStyles.base}
              onMouseOver={(e) => e.currentTarget.style.background = buttonStyles.hover}
              onMouseOut={(e) => e.currentTarget.style.background = buttonStyles.base.background}
            >
              <CloudDownloadIcon style={{ fontSize: '20px' }} />
              Exportar Excel
            </button>

            <div style={{ position: 'relative' }}>
              <input
                type="file"
                onChange={handleFileUpload}
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".xlsx,.xls,.csv"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={buttonStyles.base}
                onMouseOver={(e) => e.currentTarget.style.background = buttonStyles.hover}
                onMouseOut={(e) => e.currentTarget.style.background = buttonStyles.base.background}
              >
                <CloudUploadIcon style={{ fontSize: '20px' }} />
                Importar Excel
              </button>
              {uploadStatus.show && (
                <div style={{
                  ...uploadStatusStyles.container,
                  backgroundColor: uploadStatus.success ? uploadStatusStyles.success.backgroundColor : uploadStatusStyles.error.backgroundColor
                }}>
                  {uploadStatus.message}
                </div>
              )}
            </div>
          </div>
        )}

        <Card style={{ padding: '20px' }}>
          <Card style={{ background: Colors.GRAY_BACKGROUND, padding: '4px 4px 20px' }}>
            <PendingMaterialsTable
              profiles={profiles}
              groups={groups}
              materials={materials}
              setMaterials={setMaterials}
              ids={ids}
              getGroups={getGroups}
              getMaterials={getMaterials}
              handleViewDetails={handleViewDetails}
              handleGetDataByGroupId={handleGetDataByGroupId}
              data={data}
            />
            {summary && <SummarySection info={summary} />}
            {tableData.length > 0 && <FinalValuesTable consolidadoMaterialInfo={tableData} />}
          </Card>
        </Card>
      </div>

      <div style={{ display: detailsVisible ? 'block' : 'none' }}>
        {currentMaterial && materials && (
          <DetailsPage
            rol={1}
            groupId={ids.group}
            materialId={currentMaterial}
            materials={materials}
            handleReturnMainPage={handleReturnMainPage}
            setMaterialChanged={handleMaterialChanged}
            materialChanged={materialChanged}
            setMaterials={setMaterials}
            detailsVisible={detailsVisible}
            setDetailsVisible={setDetailsVisible}
          />
        )}
      </div>
    </>
  )
}

CategorySection.propTypes = {
  handleReturnHome: PropTypes.func.isRequired,
}

export { CategorySection }
